import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import subscriptionService from '../../services/subscriptionService';
import { AuthContext } from '../../contexts/AuthContext';
import styles from './subscription.module.css';

const UPCOMING_WINDOW_DAYS = 60;

const tabs = [
  { key: 'all', label: 'Tous' },
  { key: 'upcoming', label: 'À renouveler' },
  { key: 'active', label: 'Actifs' },
  { key: 'expired', label: 'Expirés' },
];

const getDateValue = (date) => {
  const value = new Date(date);
  return Number.isNaN(value.getTime()) ? null : value;
};

const formatDate = (date) => {
  const value = getDateValue(date);
  return value ? value.toLocaleDateString('fr-FR') : 'Date inconnue';
};

const formatPrice = (price) => {
  if (price === null || price === undefined || price === '') {
    return 'Prix indisponible';
  }

  const numericPrice = Number(price);

  if (Number.isNaN(numericPrice)) {
    return `${price} FCFA`;
  }

  return `${numericPrice.toLocaleString('fr-FR')} FCFA`;
};

const getBillingCycleLabel = (billingCycle) => (
  billingCycle === 'monthly' ? 'mois' : 'an'
);

const getBillingCycleName = (billingCycle) => {
  if (billingCycle === 'monthly') return 'Mensuel';
  if (billingCycle === 'yearly') return 'Annuel';
  return billingCycle || 'Cycle inconnu';
};

const getSubscriptionDomain = (subscription) => {
  const domainAttribute = subscription.subscription_attribute_values?.find(
    (attr) => attr.specific_attribute?.attribute_key === 'domain_name'
      || attr.specific_attribute?.attribute_name?.toLowerCase().includes('domaine')
  );

  return subscription.effective_domain
    || subscription.hosting_domain
    || domainAttribute?.value
    || 'Domaine non configuré';
};

const getStatusMeta = (subscription, daysUntilExpiration) => {
  const normalizedStatus = subscription.status === 'cancelled' ? 'canceled' : subscription.status;

  if (normalizedStatus === 'expired' || daysUntilExpiration < 0) {
    return { key: 'expired', label: 'Expiré' };
  }

  if (normalizedStatus === 'canceled') {
    return { key: 'canceled', label: 'Annulé' };
  }

  if (normalizedStatus === 'trial') {
    return { key: 'trial', label: 'Essai' };
  }

  if (normalizedStatus === 'past_due') {
    return { key: 'pastDue', label: 'En retard' };
  }

  return { key: 'active', label: 'Actif' };
};

const getDaysUntilExpiration = (subscription) => {
  const endDate = getDateValue(subscription.end_date);

  if (!endDate) {
    return null;
  }

  const today = new Date();
  today.setHours(0, 0, 0, 0);
  endDate.setHours(0, 0, 0, 0);

  return Math.ceil((endDate.getTime() - today.getTime()) / 86400000);
};

const enrichSubscription = (subscription) => {
  const daysUntilExpiration = getDaysUntilExpiration(subscription);
  const statusMeta = getStatusMeta(subscription, daysUntilExpiration ?? 0);
  const isExpired = statusMeta.key === 'expired';
  const isUpcoming = !isExpired
    && daysUntilExpiration !== null
    && daysUntilExpiration <= UPCOMING_WINDOW_DAYS;

  return {
    ...subscription,
    domain: getSubscriptionDomain(subscription),
    priceLabel: `${formatPrice(subscription.current_price ?? subscription.currentPrice)} / ${getBillingCycleLabel(subscription.billing_cycle)}`,
    billingCycleLabel: getBillingCycleName(subscription.billing_cycle),
    daysUntilExpiration,
    statusMeta,
    isExpired,
    isUpcoming,
  };
};

const getDeadlineLabel = (subscription) => {
  if (subscription.daysUntilExpiration === null) {
    return 'Échéance inconnue';
  }

  if (subscription.daysUntilExpiration < 0) {
    const daysExpired = Math.abs(subscription.daysUntilExpiration);
    return `Expiré depuis ${daysExpired} jour${daysExpired > 1 ? 's' : ''}`;
  }

  if (subscription.daysUntilExpiration === 0) {
    return "Expire aujourd'hui";
  }

  return `Expire dans ${subscription.daysUntilExpiration} jour${subscription.daysUntilExpiration > 1 ? 's' : ''}`;
};

const getActionLabel = (subscription) => {
  if (subscription.isExpired) return 'Renouveler';
  if (subscription.isUpcoming) return 'Préparer';
  return 'Gérer';
};

const SubscriptionRow = ({ subscription, featured = false }) => (
  <article className={`${styles.subscriptionRow} ${featured ? styles.featuredRow : ''} ${subscription.isExpired ? styles.expiredRow : ''}`}>
    <div className={styles.subscriptionIdentity}>
      {featured && <span className={styles.eyebrow}>Prochaine échéance</span>}
      <h3 className={styles.planName}>{subscription.plan?.name || 'Package sans nom'}</h3>
      <span className={styles.domainName}>{subscription.domain}</span>
    </div>

    <div className={styles.subscriptionMeta}>
      <div className={styles.metaLine}>
        <span className={`${styles.statusBadge} ${styles[subscription.statusMeta.key]}`}>
          {subscription.statusMeta.label}
        </span>
        <span>{subscription.billingCycleLabel}</span>
      </div>
      <div className={styles.dateRange}>
        {formatDate(subscription.start_date)} → {formatDate(subscription.end_date)}
      </div>
      <div className={`${styles.deadlineLabel} ${subscription.isUpcoming ? styles.warningDeadline : ''} ${subscription.isExpired ? styles.expiredDeadline : ''}`}>
        {getDeadlineLabel(subscription)}
      </div>
    </div>

    <div className={styles.subscriptionPriceBlock}>
      <strong className={styles.priceValue}>{subscription.priceLabel}</strong>
      <button
        type="button"
        className={`${styles.rowActionButton} ${subscription.isExpired ? styles.renewActionButton : ''}`}
        disabled
        title="Cette action sera disponible prochainement"
      >
        {featured && subscription.isUpcoming ? 'Préparer le renouvellement' : getActionLabel(subscription)}
      </button>
      {featured && (
        <button
          type="button"
          className={styles.secondaryActionButton}
          disabled
          title="Cette action sera disponible prochainement"
        >
          Voir détails
        </button>
      )}
    </div>
  </article>
);

const SubscriptionSection = ({ title, subscriptions, emptyLabel }) => (
  <section className={styles.subscriptionSection}>
    <div className={styles.sectionHeader}>
      <h3>{title}</h3>
      <span>{subscriptions.length}</span>
    </div>
    {subscriptions.length > 0 ? (
      <div className={styles.subscriptionRows}>
        {subscriptions.map((subscription) => (
          <SubscriptionRow key={subscription.id} subscription={subscription} />
        ))}
      </div>
    ) : (
      <p className={styles.emptySection}>{emptyLabel}</p>
    )}
  </section>
);

const SubscriptionList = () => {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const { isAuthenticated } = useContext(AuthContext);

  const loadSubscriptions = useCallback(async () => {
    if (!isAuthenticated) {
      setError('Vous devez être connecté pour voir vos abonnements.');
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const data = await subscriptionService.fetchSubscriptions();
      const subs = Array.isArray(data) ? data : (data.results || []);
      setSubscriptions(subs);
    } catch (err) {
      console.error('[SubscriptionList] Error loading subscriptions:', err);
      setError(
        err.response?.status === 401
          ? 'Session expirée. Veuillez vous reconnecter.'
          : 'Erreur lors de la récupération des abonnements.'
      );
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadSubscriptions();
  }, [loadSubscriptions]);

  const enrichedSubscriptions = useMemo(() => (
    subscriptions
      .map(enrichSubscription)
      .sort((a, b) => {
        const aDate = getDateValue(a.end_date)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        const bDate = getDateValue(b.end_date)?.getTime() ?? Number.MAX_SAFE_INTEGER;
        return aDate - bDate;
      })
  ), [subscriptions]);

  const filteredSubscriptions = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return enrichedSubscriptions.filter((subscription) => {
      const matchesSearch = !normalizedSearch
        || subscription.domain.toLowerCase().includes(normalizedSearch)
        || subscription.plan?.name?.toLowerCase().includes(normalizedSearch);

      if (!matchesSearch) return false;
      if (activeTab === 'upcoming') return subscription.isUpcoming;
      if (activeTab === 'active') return !subscription.isExpired && !subscription.isUpcoming;
      if (activeTab === 'expired') return subscription.isExpired;
      return true;
    });
  }, [activeTab, enrichedSubscriptions, searchTerm]);

  const upcomingSubscriptions = filteredSubscriptions.filter((subscription) => subscription.isUpcoming);
  const activeSubscriptions = filteredSubscriptions.filter((subscription) => !subscription.isExpired && !subscription.isUpcoming);
  const expiredSubscriptions = filteredSubscriptions.filter((subscription) => subscription.isExpired);
  const nextSubscription = enrichedSubscriptions.find((subscription) => !subscription.isExpired);

  const annualBudget = enrichedSubscriptions.reduce((total, subscription) => {
    const price = Number(subscription.current_price ?? subscription.currentPrice);
    if (Number.isNaN(price)) return total;
    return total + (subscription.billing_cycle === 'monthly' ? price * 12 : price);
  }, 0);

  const nextDateLabel = nextSubscription ? formatDate(nextSubscription.end_date) : 'Aucune échéance';

  if (loading) return <div className={styles.loading}>Chargement des abonnements...</div>;
  if (error) return <div className={styles.error}>{error}</div>;

  return (
    <div className={styles.listContainer}>
      <div className={styles.summaryGrid}>
        <div className={styles.summaryTile}>
          <span>Abonnements</span>
          <strong>{enrichedSubscriptions.length}</strong>
        </div>
        <div className={styles.summaryTile}>
          <span>À renouveler bientôt</span>
          <strong>{enrichedSubscriptions.filter((subscription) => subscription.isUpcoming).length}</strong>
        </div>
        <div className={styles.summaryTile}>
          <span>Budget annuel estimé</span>
          <strong>{formatPrice(annualBudget)} / an</strong>
        </div>
        <div className={styles.summaryTile}>
          <span>Prochaine échéance</span>
          <strong>{nextDateLabel}</strong>
        </div>
      </div>

      <div className={styles.listToolbar}>
        <div>
          <h2 className={styles.listTitle}>Mes abonnements</h2>
          <p className={styles.listSubtitle}>Suivez les échéances et préparez les renouvellements manuels.</p>
        </div>
        <div className={styles.toolbarControls}>
          <input
            type="search"
            className={styles.searchInput}
            placeholder="Rechercher un domaine"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
          <select className={styles.sortSelect} value="end_date" disabled>
            <option value="end_date">Échéance la plus proche</option>
          </select>
        </div>
      </div>

      <div className={styles.tabList} role="tablist" aria-label="Filtres des abonnements">
        {tabs.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`${styles.tabButton} ${activeTab === tab.key ? styles.activeTabButton : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {nextSubscription && activeTab !== 'expired' && !searchTerm && (
        <SubscriptionRow subscription={nextSubscription} featured />
      )}

      {filteredSubscriptions.length > 0 ? (
        <>
          {(activeTab === 'all' || activeTab === 'upcoming') && (
            <SubscriptionSection
              title="À renouveler bientôt"
              subscriptions={upcomingSubscriptions}
              emptyLabel="Aucun abonnement n'arrive à échéance dans les 60 prochains jours."
            />
          )}
          {(activeTab === 'all' || activeTab === 'active') && (
            <SubscriptionSection
              title="Actifs"
              subscriptions={activeSubscriptions}
              emptyLabel="Aucun abonnement actif au-delà des 60 prochains jours."
            />
          )}
          {(activeTab === 'all' || activeTab === 'expired') && (
            <SubscriptionSection
              title="Expirés"
              subscriptions={expiredSubscriptions}
              emptyLabel="Aucun abonnement expiré."
            />
          )}
        </>
      ) : (
        <p className={styles.noSubscriptions}>Aucun abonnement ne correspond à votre recherche.</p>
      )}
    </div>
  );
};

export default SubscriptionList;
